import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';
import { Button } from '@/components/design-system/Button';
import { Card, CardContent } from '@/components/ui/card';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useSubscription } from '@/hooks/useSubscription';
import { FollowingRecipesSection } from '@/components/community/FollowingRecipesSection';
import { SearchAndFilters } from '@/components/community/SearchAndFilters';
import { CommunityRecipeCard } from '@/components/community/CommunityRecipeCard';
import { useCommunityRecipes } from '@/hooks/useCommunityRecipes';
import { useAuth } from '@/components/AuthProviderWrapper';
import { isIOSDevice } from '@/utils/deviceDetection';


const CommunityGalleryPage: React.FC = () => {
  const subscription = useSubscription();
  const { isAuthenticated } = useAuth();
  const isIOS = isIOSDevice();
  const [showFilters, setShowFilters] = useState(false);

  // Use custom hook for recipe management
  const {
    recipes,
    loading,
    error,
    hasMore,
    filters,
    updateFilters,
    loadRecipes,
    loadMore
  } = useCommunityRecipes();

  // Set up infinite scroll
  const { isFetching } = useInfiniteScroll(loadMore, 100, loading, hasMore);

  return (
    <>
      <Helmet>
        <title>Community Recipes - Discover Delicious Dishes | DinnerBrain</title>
        <meta
          name="description"
          content="Discover thousands of delicious recipes shared by our cooking community. Find recipes by cuisine, diet, difficulty, and cooking time. Get inspired for your next meal!"
        />
        <meta name="keywords" content="recipes, cooking, community, food, meal planning, cuisine, diet" />
        <meta property="og:title" content="Community Recipes - Discover Delicious Dishes | DinnerBrain" />
        <meta property="og:description" content="Discover thousands of delicious recipes shared by our cooking community." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#E6EFEA' }}>
        {/* Main Content - No padding needed since AppLayout handles header positioning */}
        <div>
          {/* Header */}
          <SearchAndFilters
            filters={filters}
            showFilters={showFilters}
            onUpdateFilters={updateFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
          />

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-2 sm:px-4 py-8">
            {/* Following Recipes Section - Only for paid users */}
            {subscription.isActive && !subscription.isLoading && filters.sort !== 'following' && !filters.search && (
              <div className="mb-8">
                <FollowingRecipesSection />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600">{error}</p>
                <Button
                  variant="secondary"
                  onClick={() => loadRecipes(1, true)}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            )}

            {loading && recipes.length === 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-6">
                {Array(8).fill(0).map((_, i) => (
                  <Card key={i} className="w-full overflow-hidden animate-pulse">
                    <div className="aspect-[4/3] sm:aspect-video bg-gray-200" />
                    <CardContent className="p-3 sm:p-4 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : recipes.length === 0 ? (
              <div className="text-center py-12">
                <ChefHat className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No recipes found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or search terms to find more recipes.
                </p>
                <Button onClick={() => updateFilters({ search: '', cuisine: '', diet: '', difficulty: '', timeRange: '' })}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                {/* Results Count */}
                <div className="mb-6">
                  <p className="text-gray-600">
                    {filters.search ? `Search results for "${filters.search}"` : 'Showing all recipes'}
                    {' · '}
                    <span className="font-medium">{recipes.length} recipes</span>
                  </p>
                </div>

                {/* Recipe Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-6">
                  {recipes.map((recipe) => (
                    <CommunityRecipeCard key={recipe.id} recipe={recipe} />
                  ))}
                </div>

                {/* Load More */}
                {isFetching && (
                  <div className="mt-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading more recipes...</p>
                  </div>
                )}

                {!hasMore && recipes.length > 0 && (
                  <div className="mt-8 text-center text-gray-600">
                    <p>You&apos;ve seen all the recipes! 🍳</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CommunityGalleryPage;